import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../state/AppContext";
import { toast } from "sonner";
import { UploadCloud, X, Check, ArrowLeft, Camera, Video, AlertCircle } from "lucide-react";
import Confetti from "react-confetti";
import { DIFFICULTY_STYLES } from "../data/challenges";
import { uploadToCloudinary, isCloudinaryConfigured } from "../lib/cloudinary";
import ApplyCTA from "../components/ApplyCTA";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;   // 10 MB
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;  // 100 MB
const ACCEPTED_TYPES = "image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/quicktime,video/webm,video/x-m4v";

export default function SubmitScreen() {
  const { currentChallenge, submitChallenge, setScreen, setCurrentChallenge, t } = useApp();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [dims, setDims] = useState({ w: window.innerWidth, h: window.innerHeight });
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    const onResize = () => setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview({ url, type: file.type });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!currentChallenge) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center bg-scl-cream">
        <p className="text-scl-charcoal/70 mb-4">No challenge picked yet.</p>
        <button onClick={() => setScreen("gallery")} className="h-12 px-6 rounded-full bg-scl-coral text-white font-semibold" data-testid="go-gallery-btn">
          Browse challenges
        </button>
      </div>
    );
  }

  const handleFile = (f) => {
    if (!f) return;
    const isVideo = f.type.startsWith("video/");
    const isImage = f.type.startsWith("image/");
    if (!isVideo && !isImage) {
      toast.error("Please pick a photo or video");
      return;
    }
    const cap = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
    if (f.size > cap) {
      toast.error(isVideo ? "Video must be under 100 MB" : "Photo must be under 10 MB");
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (submitting) return;
    if (!file) { toast.error("Please add a photo or video first"); return; }
    setSubmitting(true);
    setProgress(0);
    try {
      let media_url = null;
      let media_type = null;
      if (isCloudinaryConfigured()) {
        const result = await uploadToCloudinary(file, (p) => setProgress(p));
        media_url = result.url;
        media_type = result.resource_type === "video" ? "video" : "image";
      } else {
        // Fallback: not configured — surface a clear error to the user
        throw new Error("Media uploads aren't configured yet. Please contact the SCL team.");
      }
      await submitChallenge({ challenge: currentChallenge, media_url, media_type, caption });
      setSuccess(true);
    } catch (e) {
      toast.error(e?.message || "Submission failed — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-scl-cream flex flex-col items-center px-6 pt-16 pb-40 relative overflow-hidden" data-testid="submit-success">
        <Confetti width={Math.min(dims.w, 480)} height={dims.h} numberOfPieces={220} recycle={false} colors={["#F08C5D", "#F4C94E", "#2E5E5C", "#B14A3A"]} />
        <div className="w-24 h-24 rounded-full bg-scl-teal flex items-center justify-center shadow-warm mb-6 animate-pop-in">
          <Check className="w-14 h-14 text-white" strokeWidth={3} />
        </div>
        <div className="px-5 py-2 rounded-full bg-scl-mustard text-scl-charcoal font-bold text-lg mb-5 animate-fade-up" data-testid="points-earned">
          +{currentChallenge.points} points
        </div>
        <h2 className="font-serif text-3xl text-center text-scl-charcoal font-bold mb-2 animate-fade-up">Amazing! You earned<br />{currentChallenge.points} points.</h2>
        <p className="text-scl-charcoal/70 text-center max-w-xs animate-fade-up mb-8">Keep the kindness going. One act at a time — Indianapolis notices.</p>

        <div className="w-full max-w-md animate-fade-up">
          <ApplyCTA />
        </div>

        <button
          onClick={() => { setCurrentChallenge(null); setScreen("mychallenges"); }}
          data-testid="continue-btn"
          className="mt-6 h-12 px-6 rounded-full bg-white border border-scl-border text-scl-charcoal font-medium hover:bg-scl-cream active:scale-95 transition-all min-h-[44px]"
        >
          {t("continue_to_mine")}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-scl-cream pb-40 pt-6 px-6" data-testid="submit-screen">
      <button onClick={() => setScreen("detail")} className="flex items-center gap-1 text-sm text-scl-charcoal/70 mb-4 hover:text-scl-teal" data-testid="back-to-detail">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.18em] text-scl-teal font-semibold mb-1">Your challenge</div>
        <h2 className="font-serif text-3xl text-scl-charcoal font-bold leading-tight">Record or upload your<br/> kindness moment.</h2>
      </div>

      <div className="bg-white rounded-2xl shadow-warm-sm mb-5 overflow-hidden">
        <div className="relative h-28">
          <img src={currentChallenge.image} alt="Challenge" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-scl-charcoal/70 via-scl-charcoal/10 to-transparent" />
          <div className="absolute top-2.5 left-3 right-3 flex items-center gap-2 flex-wrap">
            <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${DIFFICULTY_STYLES[currentChallenge.difficulty].bg} ${DIFFICULTY_STYLES[currentChallenge.difficulty].text}`}>
              {currentChallenge.difficulty}
            </span>
            <span className="px-2.5 py-0.5 rounded-full bg-white/95 text-scl-charcoal text-[11px] font-semibold uppercase tracking-wider">{currentChallenge.age}</span>
            <span className="ml-auto px-2.5 py-0.5 rounded-full bg-scl-mustard text-scl-charcoal text-[11px] font-bold">{currentChallenge.points} pts</span>
          </div>
        </div>
        <div className="p-5">
          <div className="font-serif text-lg text-scl-charcoal font-bold leading-snug" data-testid="accepted-challenge-title">{currentChallenge.title}</div>
        </div>
      </div>

      {!isCloudinaryConfigured() && (
        <div className="mb-4 p-3 rounded-xl bg-scl-brick/10 border border-scl-brick/30 flex items-start gap-2" data-testid="cloudinary-warning">
          <AlertCircle className="w-4 h-4 text-scl-brick mt-0.5 flex-shrink-0" />
          <div className="text-[12px] text-scl-brick leading-snug">
            Media uploads aren&apos;t configured yet. Ask the SCL team to set the Cloudinary keys before submitting.
          </div>
        </div>
      )}

      {/* Upload */}
      {!file ? (
        <div>
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="block border-2 border-dashed border-scl-teal/40 bg-white/60 rounded-2xl p-8 text-center cursor-pointer hover:bg-white/90 transition-all"
            data-testid="upload-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              className="sr-only"
              data-testid="file-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <div className="w-14 h-14 rounded-full bg-scl-teal/10 flex items-center justify-center mx-auto mb-3">
              <UploadCloud className="w-7 h-7 text-scl-teal" />
            </div>
            <div className="font-serif text-lg text-scl-charcoal font-bold mb-1">Record or upload your kindness moment</div>
            <div className="text-sm text-scl-charcoal/60">Photos up to 10 MB · Videos up to 100 MB</div>
          </label>

          {/* Camera-capture shortcut for mobile */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            className="sr-only"
            data-testid="camera-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <button
              onClick={() => cameraRef.current?.click()}
              data-testid="camera-btn"
              className="h-12 rounded-xl bg-white border border-scl-border text-scl-charcoal font-medium flex items-center justify-center gap-2 hover:bg-scl-cream active:scale-95 transition-all"
            >
              <Camera className="w-4 h-4 text-scl-teal" /> Use camera
            </button>
            <button
              onClick={() => inputRef.current?.click()}
              data-testid="choose-file-btn"
              className="h-12 rounded-xl bg-white border border-scl-border text-scl-charcoal font-medium flex items-center justify-center gap-2 hover:bg-scl-cream active:scale-95 transition-all"
            >
              <Video className="w-4 h-4 text-scl-coral" /> Choose file
            </button>
          </div>
        </div>
      ) : (
        <div className="relative rounded-2xl overflow-hidden bg-white shadow-warm-sm" data-testid="upload-preview">
          {preview?.type?.startsWith("video/") ? (
            <video src={preview.url} controls playsInline className="w-full max-h-80 object-cover bg-black" />
          ) : (
            <img src={preview?.url} alt="preview" className="w-full max-h-80 object-cover" />
          )}
          <button
            onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; if (cameraRef.current) cameraRef.current.value = ""; }}
            data-testid="clear-file"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
            disabled={submitting}
          >
            <X className="w-5 h-5" />
          </button>
          <div className="px-3 py-2 text-[11px] text-scl-charcoal/70 flex items-center justify-between border-t border-scl-border">
            <span>{(file.size / (1024 * 1024)).toFixed(1)} MB · {file.type.split("/")[0]}</span>
            {submitting && progress > 0 && <span data-testid="upload-progress">{Math.round(progress * 100)}%</span>}
          </div>
        </div>
      )}

      {submitting && (
        <div className="mt-3 h-1.5 rounded-full bg-scl-border overflow-hidden">
          <div className="h-full bg-scl-teal transition-all" style={{ width: `${Math.max(6, Math.round(progress * 100))}%` }} />
        </div>
      )}

      <div className="mt-5">
        <label className="text-xs uppercase tracking-widest text-scl-charcoal/60 font-semibold">Caption (optional)</label>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={3}
          data-testid="caption-input"
          placeholder="Tell us what happened…"
          className="mt-2 w-full rounded-xl border border-scl-border bg-white px-4 py-3 text-base focus:border-scl-teal focus:outline-none focus:ring-2 focus:ring-scl-teal/20 transition-all"
        />
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40">
        <div className="max-w-[480px] mx-auto px-6 pb-24 pt-3 bg-gradient-to-t from-scl-cream via-scl-cream/95 to-transparent">
          <p className="text-[11px] text-scl-charcoal/55 leading-snug mb-2 text-center px-2" data-testid="media-use-notice">
            By submitting, you confirm you have permission to share this content and agree to SCL&apos;s media use policy.
          </p>
          <button
            onClick={submit}
            disabled={submitting || !file}
            data-testid="submit-challenge-btn"
            className="w-full h-14 rounded-full bg-scl-coral text-white font-semibold text-lg shadow-warm hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-60"
          >
            {submitting ? (progress > 0 ? `Uploading… ${Math.round(progress * 100)}%` : "Uploading…") : "Submit my challenge!"}
          </button>
        </div>
      </div>
    </div>
  );
}
